// libs
import React, { memo } from 'react';
import { styled } from '@nykaa/ui-components';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// components
import WidgetTopRef from 'shared/layouts/DetailsPage/components/WidgetLoader/components/WidgetTopRef';
// import AdPlatformWidgets from './renderer';
import RenderBannerWidget from './components/RenderBannerWidget';
import MobileWidgetFeed from 'shared/layouts/DetailsPage/components/Widgets/components/WidgetFeed/Mobile';
import SingleWidget from './components/SingleWidget';
import RenderCAV from './components/RenderCav';
import RenderBasicSlider from './components/RenderBasicSlider';

// helpers
import { getRecentViewedPDP } from 'shared/layouts/DetailsPage/components/RecentlyViewedObserver/helpers';
import {
  getTransformedWidgetData,
  getBannerConfig,
} from 'shared/layouts/DetailsPage/components/Widgets/helpers';

// hooks
import useCustomerGlue from 'shared/hooks/useCustomerGlue';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import useWidgetLoader from 'shared/layouts/DetailsPage/components/WidgetLoader/useWidgetLoader';

// constants
import { WIDGET_TYPES } from './constant';

// defs
import { IWidgetListProps, IWidgetData, IProduct } from './types';

// styles
const Container = styled.div`
  content-visibility: auto;
  contain-intrinsic-size: 1900px;
`;

function WidgetList({ widgetData, productID, isMobile = false }: IWidgetListProps) {
  const { widgetConfig, transformedWidgetData } = useWidgetLoader({ isMobile });
  const widgetLayout = isMobile ? transformedWidgetData : {};

  const { bannerIndex, bannerWidgetConfig } = getBannerConfig(widgetConfig);
  // customer glue experiement data
  const { isCGEnabled } = useCustomerGlue();
  // customer glue pdp widget config
  const cgBannerWidgetConfig = useRemoteConfig('pdpCGBannerWidget');

  // if user segmented to customer glue experiment show customer glue widget else default widget
  const finalBannerWgtCfg = isCGEnabled ? cgBannerWidgetConfig : bannerWidgetConfig;

  if (isEmpty(widgetData.relatedProducts)) {
    return null;
  }

  let modifiedWidgetData = widgetData;

  if (finalBannerWgtCfg) {
    modifiedWidgetData = getTransformedWidgetData(
      widgetData,
      isMobile,
      finalBannerWgtCfg,
      bannerIndex
    );
  }

  return (
    <Container>
      {modifiedWidgetData.relatedProducts.map((widget: IWidgetData, i: number) => {
        const widgetType = get(widget, 'widget_type', '').toLowerCase();

        switch (widgetType) {
          case WIDGET_TYPES.PAIR_IT_WITH: {
            return (
              <SingleWidget
                widgetType={WIDGET_TYPES.PAIR_IT_WITH}
                widgetData={widget}
                index={i}
                isMobile={isMobile}
                key={i}
              />
            );
          }
          case WIDGET_TYPES.CUSTOMER_ALSO_VIEWED:
            return <RenderCAV i={i} widget={widget} key={i} ppid={productID} />;

          case WIDGET_TYPES.BANNER:
            if (!isMobile) {
              return null;
            }
            return (
              <RenderBannerWidget
                key={i}
                bannerTilePosition={i}
                widget={widget}
                productID={productID}
              />
            );

          case WIDGET_TYPES.SIMILAR_PRODUCTS:
            // for mobile similar products are rendered on top
            if (isMobile) {
              return null;
            }
            // this is kept for desktop
            return (
              <SingleWidget
                widgetType={WIDGET_TYPES.SIMILAR_PRODUCTS}
                widgetData={widget}
                index={i}
                isMobile={isMobile}
                key={i}
              />
            );

          case WIDGET_TYPES.MORE_FROM_BRAND:
            return (
              <SingleWidget
                widgetType={WIDGET_TYPES.MORE_FROM_BRAND}
                widgetData={widget}
                index={i}
                isMobile={isMobile}
                key={i}
                widgetLayout={widgetLayout}
                showTopBorder={true}
              />
            );

          case WIDGET_TYPES.MORE_FROM_CATEGORY:
            return (
              <SingleWidget
                widgetType={WIDGET_TYPES.MORE_FROM_CATEGORY}
                widgetData={widget}
                index={i}
                isMobile={isMobile}
                key={i}
                widgetLayout={widgetLayout}
                showTopBorder={true}
              />
            );

          default: {
            if (widget.is_recently_viewed) {
              let recentlyViewedPDP = getRecentViewedPDP();

              if (recentlyViewedPDP.length <= 1) {
                return null;
              }

              // During refactoring move this into helper method
              recentlyViewedPDP = recentlyViewedPDP
                .map((item: IProduct) => {
                  const actionURL = `${item.action_url}?intcmp=widget,recently_viewed&ppid=${productID}`;
                  return { ...item, action_url: actionURL };
                })
                .slice(1);

              const recentlyViewedData = {
                ...widget,
                products: recentlyViewedPDP,
              };

              const displayType = widgetLayout && widgetLayout[WIDGET_TYPES.RECENTLY_VIEWED];
              if (isMobile && displayType === 'feed') {
                return <MobileWidgetFeed widget={recentlyViewedData} key={i} index={i} />;
              }

              return (
                <RenderBasicSlider
                  widget={recentlyViewedData}
                  i={i}
                  key={i}
                  isDesktop
                  showTopBorder={true}
                />
              );
            }

            return (
              <React.Fragment key={i}>
                {widgetType === WIDGET_TYPES.SIMILAR_PRODUCTS && <WidgetTopRef />}
                <RenderBasicSlider widget={widget} i={i} isDesktop />
              </React.Fragment>
            );
          }
        }
      })}
    </Container>
  );
}

export default memo(WidgetList);
